/**
 * `returnTo` — carrying a deep link's destination through the sign-in round-trip
 * (IMM-486). An unauthenticated cold click on an in-app deep link (the normal
 * case for an e-mail CTA) is redirected to `/sign-in`; without a returnTo the
 * destination is discarded and the user lands on the workspace home instead of
 * the page they clicked. These helpers thread the intended path through the
 * guard -> `/sign-in` -> WorkOS `state` -> callback, and — crucially — validate
 * it as a same-origin app path before anything navigates to it.
 */

// Control characters (C0 range 0x00-0x1f + DEL 0x7f) are rejected outright —
// defence against a smuggled CR/LF or NUL in a returnTo value. Checked by code
// point rather than a control-char regex literal (which trips `no-control-regex`).
function hasControlChar(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code <= 0x1f || code === 0x7f) return true;
  }

  return false;
}

/**
 * Build the `/sign-in` redirect a route guard throws when it turns an
 * unauthenticated visitor away, preserving where they were going. `dest` is the
 * current in-app location (`pathname + search`); it is re-validated on the way
 * back out in `safeReturnTo`, so this only needs to carry it.
 */
export function buildSignInRedirect(dest: string): string {
  return `/sign-in?returnTo=${encodeURIComponent(dest)}`;
}

/**
 * A value is a safe navigation target only if it is a same-origin **app path** —
 * it must start with a single `/`. Everything else is rejected so a hostile or
 * malformed `returnTo` can never bounce the user off-origin:
 *  - `//evil.example` / `/\evil` are protocol-relative URLs the browser treats
 *    as off-origin — a naive `startsWith('/')` alone lets them through.
 *  - `https://…` / `javascript:…` do not start with `/`, so the scheme (a `:`
 *    before the first `/`) is rejected implicitly by the leading-slash rule.
 *  - control characters are rejected outright (defence against smuggled CR/LF).
 */
function isSafeAppPath(value: string): boolean {
  if (!value.startsWith('/')) return false;

  // Protocol-relative: a second `/` or `\` makes it an off-origin authority.
  if (value.startsWith('//') || value.startsWith('/\\')) return false;

  if (hasControlChar(value)) return false;

  return true;
}

/**
 * Decode a base64url string to UTF-8. The WorkOS `state` is minted server-side
 * with `Buffer.from(json).toString('base64url')`; the browser's `atob` only
 * understands standard base64, so translate the alphabet and re-pad first, then
 * decode the bytes as UTF-8 (a returnTo may contain non-ASCII).
 */
function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.length % 4 === 0 ? base64 : base64 + '='.repeat(4 - (base64.length % 4));
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder().decode(bytes);
}

/**
 * Extract the intended destination from the WorkOS `state` echoed on the
 * callback URL, or `null` when there is none / it is not trustworthy. The state
 * is `base64url(JSON.stringify({ n, r }))` where `r` is the returnTo (or `null`).
 * Any failure to decode, parse, or validate is treated as "no destination" so
 * the caller falls back to `/` — a malformed state must never surface an error
 * to the user, and a non-same-origin `r` must never be navigated to.
 */
export function safeReturnTo(state: string | null): string | null {
  if (state === null || state.length === 0) return null;

  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(state));

    if (typeof parsed !== 'object' || parsed === null) return null;

    const returnTo = (parsed as { r?: unknown }).r;

    if (typeof returnTo !== 'string') return null;

    return isSafeAppPath(returnTo) ? returnTo : null;
  } catch {
    return null;
  }
}
